import React, { useState, useEffect } from 'react';
import { Container, Card, Form, Button, Alert, Row, Col, Spinner } from 'react-bootstrap';
import { useNavigate, Link } from 'react-router-dom';
import { FiArrowLeft, FiSave, FiUser, FiMail, FiShield, FiLock } from 'react-icons/fi';
import api from '../services/api';
import { encryptPassword } from '../utils/auth';

const CreateUser = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        nome: '',
        email: '',
        cpf: '',
        matricula: '',
        senha: '',
        profile_id: '' // Alterado de 'perfil' para 'profile_id'
    });
    const [profiles, setProfiles] = useState([]); // Estado para armazenar perfis
    const [loading, setLoading] = useState(false);
    const [feedback, setFeedback] = useState({ type: '', msg: '' });
    const [validated, setValidated] = useState(false);

    // Carregar perfis ao montar o componente
    useEffect(() => {
        fetchProfiles();
    }, []);

    const fetchProfiles = async () => {
        try {
            const response = await api.get('/profiles');
            setProfiles(response.data);
        } catch (error) {
            console.error('Erro ao carregar perfis', error);
            setFeedback({ type: 'warning', msg: 'Não foi possível carregar a lista de perfis. Verifique a conexão.' });
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const form = e.currentTarget;
        
        if (form.checkValidity() === false) {
            e.stopPropagation();
            setValidated(true);
            return;
        }

        setValidated(true);
        setLoading(true);
        setFeedback({ type: '', msg: '' });

        try {
            const payload = { 
                ...formData, 
                senha: encryptPassword(formData.senha),
                // Enviar o nome do perfil também para compatibilidade legada se necessário, 
                // mas focando no profile_id
                perfil: profiles.find(p => p.id === parseInt(formData.profile_id))?.nome || ''
            };
            
            await api.post('/register', payload);
            setFeedback({ type: 'success', msg: 'Usuário cadastrado com sucesso! Redirecionando...' });
            setTimeout(() => navigate('/user-management'), 2000);
        } catch (err) {
            const errorMsg = err.response?.data?.msg || 'Erro ao cadastrar usuário. Tente novamente.';
            setFeedback({ type: 'danger', msg: errorMsg });
            setLoading(false);
        }
    };

    return (
        <Container className="py-5" style={{ maxWidth: '800px' }}>
            <div className="d-flex align-items-center mb-4">
                <Button variant="link" 
                        as={Link} 
                        to="/user-management" 
                        className="text-decoration-none p-0 mb-1 text-muted"
                >
                <FiArrowLeft /> Voltar para Gestão de usuários
                </Button>
            </div>
            <div>
                <h2 className="mb-4 fw-bold" style={{ fontFamily: 'Nunito, sans-serif', color: '#006064' }}>
                    Novo Usuário
                </h2>
            </div>
            <Card className="shadow-sm border-0 rounded-4">
                <Card.Body className="p-4 p-md-5">
                    {feedback.msg && <Alert variant={feedback.type} dismissible onClose={() => setFeedback({ type: '', msg: '' })}>{feedback.msg}</Alert>}
                    
                    <Form noValidate validated={validated} onSubmit={handleSubmit}>
                        <Row className="g-4">
                            <Col md={12}>
                                <h5 className="mb-3 text-primary d-flex align-items-center gap-2">
                                    <FiUser /> Informações Pessoais
                                </h5>
                            </Col>
                            
                            <Col md={12}>
                                <Form.Group controlId="nome">
                                    <Form.Label>Nome Completo</Form.Label>
                                    <Form.Control 
                                        type="text" 
                                        name="nome"
                                        value={formData.nome}
                                        onChange={handleChange}
                                        placeholder="Ex: João da Silva"
                                        required 
                                        className="py-2"
                                    />
                                    <Form.Control.Feedback type="invalid">Por favor, informe o nome completo.</Form.Control.Feedback>
                                </Form.Group>
                            </Col>

                            <Col md={6}>
                                <Form.Group controlId="cpf">
                                    <Form.Label>CPF</Form.Label>
                                    <Form.Control 
                                        type="text" 
                                        name="cpf"
                                        value={formData.cpf}
                                        onChange={handleChange}
                                        placeholder="000.000.000-00"
                                        required 
                                        className="py-2"
                                    />
                                    <Form.Control.Feedback type="invalid">CPF é obrigatório.</Form.Control.Feedback>
                                </Form.Group>
                            </Col>

                            <Col md={6}>
                                <Form.Group controlId="matricula">
                                    <Form.Label>Matrícula / PIN</Form.Label>
                                    <Form.Control 
                                        type="text" 
                                        name="matricula"
                                        value={formData.matricula}
                                        onChange={handleChange}
                                        placeholder="Ex: 123456"
                                        required 
                                        className="py-2"
                                    />
                                    <Form.Control.Feedback type="invalid">Matrícula é obrigatória.</Form.Control.Feedback>
                                </Form.Group>
                            </Col>

                            <Col md={12}>
                                <hr className="my-2 opacity-10" />
                                <h5 className="mb-3 mt-2 text-primary d-flex align-items-center gap-2">
                                    <FiLock /> Acesso e Segurança
                                </h5>
                            </Col>

                            <Col md={12}>
                                <Form.Group controlId="email">
                                    <Form.Label>Email Corporativo</Form.Label>
                                    <div className="input-group">
                                        <span className="input-group-text bg-light border-end-0"><FiMail /></span>
                                        <Form.Control 
                                            type="email" 
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            placeholder="nome@empresa.com"
                                            required 
                                            className="py-2 border-start-0"
                                        />
                                        <Form.Control.Feedback type="invalid">Informe um email válido.</Form.Control.Feedback>
                                    </div>
                                </Form.Group>
                            </Col>

                            <Col md={6}>
                                <Form.Group controlId="senha">
                                    <Form.Label>Senha Inicial</Form.Label>
                                    <Form.Control 
                                        type="password" 
                                        name="senha"
                                        value={formData.senha}
                                        onChange={handleChange}
                                        placeholder="••••••••"
                                        required 
                                        minLength={6}
                                        className="py-2"
                                    />
                                    <Form.Control.Feedback type="invalid">A senha deve ter pelo menos 6 caracteres.</Form.Control.Feedback>
                                </Form.Group>
                            </Col>

                            <Col md={6}>
                                <Form.Group controlId="profile_id">
                                    <Form.Label>Perfil de Acesso</Form.Label>
                                    <div className="input-group">
                                        <span className="input-group-text bg-light border-end-0"><FiShield /></span>
                                        <Form.Select 
                                            name="profile_id"
                                            value={formData.profile_id}
                                            onChange={handleChange}
                                            className="py-2 border-start-0"
                                            required
                                        >
                                            <option value="">Selecione...</option>
                                            {profiles.map(profile => (
                                                <option key={profile.id} value={profile.id}>
                                                    {profile.nome}
                                                </option>
                                            ))}
                                        </Form.Select>
                                        <Form.Control.Feedback type="invalid">Selecione um perfil de acesso.</Form.Control.Feedback>
                                    </div>
                                </Form.Group>
                            </Col>
                        </Row>

                        <div className="d-flex justify-content-end mt-5 gap-3">
                            <Button variant="light" as={Link} to="/user-management" className="px-4">
                                Cancelar
                            </Button>
                            <Button variant="primary" type="submit" disabled={loading} className="px-4 d-flex align-items-center gap-2">
                                {loading ? <Spinner size="sm" animation="border" /> : <FiSave />}
                                {loading ? 'Salvando...' : 'Cadastrar Usuário'}
                            </Button>
                        </div>
                    </Form>
                </Card.Body>
            </Card>
        </Container>
    );
};

export default CreateUser;
